import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home, Megaphone, Brain, Handshake, Star, CalendarDays, Network,
  User, Mail, HelpCircle, Shield, RefreshCw, Search,
  type LucideIcon,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

interface PageResult { kind: "page"; label: string; labelAr?: string; path: string; icon: LucideIcon; }
interface RecordResult {
  kind: "influencer" | "event" | "partner" | "campaign";
  id: string;
  label: string;
  meta?: string;
  path: string;
}

const PAGES: PageResult[] = [
  { kind: "page", label: "Home", labelAr: "الرئيسية", path: "/", icon: Home },
  { kind: "page", label: "Create Campaign", labelAr: "إنشاء حملة", path: "/campaign", icon: Megaphone },
  { kind: "page", label: "AI Campaign Creator", labelAr: "منشئ الحملات بالذكاء الاصطناعي", path: "/ai-campaign-creator", icon: Brain },
  { kind: "page", label: "Become an Affiliate", labelAr: "كن شريكًا", path: "/affiliate-program", icon: Handshake },
  { kind: "page", label: "Influencers", labelAr: "المؤثرون", path: "/influencers", icon: Star },
  { kind: "page", label: "Events", labelAr: "الفعاليات", path: "/events-sponsors", icon: CalendarDays },
  { kind: "page", label: "Network", labelAr: "الشبكة", path: "/partners", icon: Network },
  { kind: "page", label: "Who We Are", labelAr: "من نحن", path: "/who-we-are", icon: User },
  { kind: "page", label: "Contact", labelAr: "تواصل", path: "/contact", icon: Mail },
  { kind: "page", label: "Help & FAQs", labelAr: "المساعدة", path: "/faqs", icon: HelpCircle },
  { kind: "page", label: "Privacy Policy", labelAr: "سياسة الخصوصية", path: "/privacy-policy", icon: Shield },
  { kind: "page", label: "Return Policy", labelAr: "سياسة الاسترداد", path: "/return-policy", icon: RefreshCw },
];

interface Props { open: boolean; onOpenChange: (open: boolean) => void; }

export const GlobalSearchDialog = ({ open, onOpenChange }: Props) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [query, setQuery] = useState("");
  const [influencers, setInfluencers] = useState<RecordResult[]>([]);
  const [events, setEvents] = useState<RecordResult[]>([]);
  const [partners, setPartners] = useState<RecordResult[]>([]);
  const [campaigns, setCampaigns] = useState<RecordResult[]>([]);
  const isAr = language === "ar";

  // Keyboard shortcut Cmd/Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  // Debounced remote search
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setInfluencers([]); setEvents([]); setPartners([]); setCampaigns([]);
      return;
    }
    const t = setTimeout(async () => {
      const like = `%${q}%`;
      const tasks: Promise<unknown>[] = [];

      tasks.push(
        (async () => {
          const { data } = await supabase
            .from("influencers")
            .select("id,name,category")
            .eq("approval_status", "approved")
            .ilike("name", like)
            .limit(5);
          setInfluencers(
            (data ?? []).map((r) => ({
              kind: "influencer" as const,
              id: r.id,
              label: r.name,
              meta: r.category ?? undefined,
              path: `/influencers/${r.id}`,
            })),
          );
        })(),
      );

      tasks.push(
        (async () => {
          const { data } = await supabase
            .from("event_organizers")
            .select("id,name,category")
            .eq("approval_status", "approved")
            .ilike("name", like)
            .limit(5);
          setEvents(
            (data ?? []).map((r) => ({
              kind: "event" as const,
              id: r.id,
              label: r.name,
              meta: r.category ?? undefined,
              path: `/events-sponsors`,
            })),
          );
        })(),
      );

      if (user) {
        tasks.push(
          (async () => {
            const { data } = await supabase
              .from("campaigns")
              .select("id,brand_name,company_name,status")
              .or(`brand_name.ilike.${like},company_name.ilike.${like}`)
              .limit(5);
            setCampaigns(
              (data ?? []).map((r) => ({
                kind: "campaign" as const,
                id: r.id,
                label: r.brand_name || r.company_name || "Campaign",
                meta: r.status ?? undefined,
                path: `/client-dashboard`,
              })),
            );
          })(),
        );
      } else {
        setCampaigns([]);
      }

      tasks.push(
        (async () => {
          const { data } = await supabase
            .from("media_owner_locations")
            .select("media_owner_name,location_name")
            .or(`media_owner_name.ilike.${like},location_name.ilike.${like}`)
            .limit(5);
          const seen = new Set<string>();
          const rows: RecordResult[] = [];
          for (const r of data ?? []) {
            const key = r.media_owner_name;
            if (key && !seen.has(key)) {
              seen.add(key);
              rows.push({
                kind: "partner",
                id: key,
                label: key,
                meta: r.location_name ?? undefined,
                path: `/partners`,
              });
            }
          }
          setPartners(rows);
        })(),
      );

      await Promise.all(tasks);
    }, 220);
    return () => clearTimeout(t);
  }, [query, open, user]);

  const filteredPages = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PAGES;
    return PAGES.filter((p) =>
      p.label.toLowerCase().includes(q) || (p.labelAr ?? "").includes(query),
    );
  }, [query]);

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder={isAr ? "ابحث عن صفحات، مؤثرين، شركاء، فعاليات…" : "Search pages, influencers, partners, events…"}
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[460px]">
        <CommandEmpty>
          <div className="flex flex-col items-center gap-2 py-6 text-muted-foreground">
            <Search className="h-5 w-5 opacity-60" />
            <span className="text-sm">
              {isAr ? "لا توجد نتائج" : "No results found"}
            </span>
          </div>
        </CommandEmpty>

        {filteredPages.length > 0 && (
          <CommandGroup heading={isAr ? "الصفحات" : "Pages"}>
            {filteredPages.map((p) => {
              const Icon = p.icon;
              return (
                <CommandItem
                  key={p.path}
                  value={`page-${p.label}-${p.labelAr ?? ""}`}
                  onSelect={() => go(p.path)}
                  className="gap-3"
                >
                  <Icon className="h-4 w-4 text-primary" />
                  <span>{isAr && p.labelAr ? p.labelAr : p.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {influencers.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={isAr ? "المؤثرون" : "Influencers"}>
              {influencers.map((r) => (
                <CommandItem key={r.id} value={`inf-${r.label}`} onSelect={() => go(r.path)} className="gap-3">
                  <Star className="h-4 w-4 text-primary" />
                  <span className="flex-1">{r.label}</span>
                  {r.meta && <span className="text-xs text-muted-foreground">{r.meta}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {events.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={isAr ? "الفعاليات" : "Events"}>
              {events.map((r) => (
                <CommandItem key={r.id} value={`evt-${r.label}`} onSelect={() => go(r.path)} className="gap-3">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  <span className="flex-1">{r.label}</span>
                  {r.meta && <span className="text-xs text-muted-foreground">{r.meta}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {partners.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={isAr ? "الشركاء والمواقع" : "Partners & locations"}>
              {partners.map((r) => (
                <CommandItem key={r.id} value={`prt-${r.label}`} onSelect={() => go(r.path)} className="gap-3">
                  <Network className="h-4 w-4 text-primary" />
                  <span className="flex-1">{r.label}</span>
                  {r.meta && <span className="text-xs text-muted-foreground truncate max-w-[180px]">{r.meta}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {campaigns.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={isAr ? "حملاتي" : "Your campaigns"}>
              {campaigns.map((r) => (
                <CommandItem key={r.id} value={`cmp-${r.label}`} onSelect={() => go(r.path)} className="gap-3">
                  <Megaphone className="h-4 w-4 text-primary" />
                  <span className="flex-1">{r.label}</span>
                  {r.meta && <span className="text-xs text-muted-foreground capitalize">{r.meta}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};
